import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEOHead from '../../components/SEOHead';
import img1 from '../../assets/marketplaces2.png';
import img2 from '../../assets/marketplaces.png';
import './ServicePage.css';

const SEO = {
  title: 'Meesho Seller Management Services | Mad Marketing Club',
  description: 'Meesho seller management services in India — account setup, catalog optimization, Meesho Ads, pricing strategy & performance monitoring. Scale your Meesho sales today.',
  canonical: 'https://madmarketingclub.com/services/meesho',
  breadcrumb: [
    { name: 'Home', url: 'https://madmarketingclub.com/' },
    { name: 'Services', url: 'https://madmarketingclub.com/#services' },
    { name: 'Meesho', url: 'https://madmarketingclub.com/services/meesho' },
  ],
  service: { name: 'Meesho Seller Management Services', type: 'Meesho Marketplace Management' },
};

const OVERVIEW_POINTS = [
  'Access crores of customers across India',
  '0% commission selling model',
  'PAN India delivery network',
  'Meesho Ads for product promotion',
  'Easy supplier onboarding',
  'Order and inventory management tools',
  'Seller analytics and reporting',
  'Dedicated supplier panel for operations',
];

const APPROACH = [
  'Meesho seller onboarding',
  'Catalog creation & optimization',
  'Meesho SEO',
  'Meesho Ads management',
  'Inventory planning',
  'Pricing optimization',
  'Order performance monitoring',
  'Account health management',
  'Revenue growth strategies',
];

const PROCESS = [
  {
    num: '01',
    title: 'Business Consultation & Discovery',
    intro: 'We begin by understanding:',
    points: [
      'Product categories',
      'Current sales performance',
      'Target customers',
      'Competitive landscape',
      'Profit margins',
      'Growth objectives',
    ],
  },
  {
    num: '02',
    title: 'Meesho Account Audit',
    intro: 'We evaluate:',
    points: [
      'Catalog quality',
      'Product visibility',
      'Listing optimization',
      'Pricing competitiveness',
      'Ad performance',
      'Seller performance metrics',
      'Operational efficiency',
    ],
  },
  {
    num: '03',
    title: 'Growth Strategy Development',
    intro: 'Our marketplace roadmap includes:',
    points: [
      'Catalog optimization strategy',
      'Keyword planning',
      'Advertising roadmap',
      'Inventory planning',
      'Pricing strategy',
      'Sales growth opportunities',
    ],
  },
  {
    num: '04',
    title: 'Implementation & Optimization',
    intro: 'Our team executes:',
    points: [
      'Product listings',
      'Catalog enhancements',
      'SEO optimization',
      'Advertising campaigns',
      'Pricing updates',
      'Inventory improvements',
      'Performance optimization',
    ],
  },
  {
    num: '05',
    title: 'Reporting & Scaling',
    intro: 'Monthly and weekly reporting includes:',
    points: [
      'Sales growth',
      'Product performance',
      'Advertising ROI',
      'Catalog performance',
      'Inventory health',
      'Marketplace opportunities',
    ],
  },
];

const SERVICES = [
  {
    title: 'Meesho Seller Account Setup & Onboarding',
    gradient: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
    points: [
      'Seller account registration assistance',
      'Supplier Panel setup',
      'GST verification support',
      'Pickup address setup',
      'Bank account configuration',
      'Store profile optimization',
      'Marketplace onboarding',
    ],
  },
  {
    title: 'Product Listing & Catalog Management',
    gradient: 'linear-gradient(135deg, #2563eb, #0891b2)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14,2 14,8 20,8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10,9 9,9 8,9" />
      </svg>
    ),
    points: [
      'Product listing creation',
      'Bulk catalog uploads',
      'Catalog optimization',
      'Product attribute enhancement',
      'Variation setup',
      'Listing issue resolution',
      'Category mapping',
      'Product data management',
    ],
  },
  {
    title: 'Meesho SEO Services',
    gradient: 'linear-gradient(135deg, #7c3aed, #a855f7)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    ),
    points: [
      'Keyword research',
      'Product title optimization',
      'Catalog SEO',
      'Product description optimization',
      'Search visibility enhancement',
      'Competitor analysis',
      'Ranking improvement strategies',
    ],
  },
  {
    title: 'Meesho Ads Management',
    gradient: 'linear-gradient(135deg, #ea580c, #f59e0b)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
        <line x1="2" y1="20" x2="22" y2="20" />
      </svg>
    ),
    points: [
      'Campaign setup',
      'Sponsored catalog management',
      'Keyword targeting',
      'Budget allocation',
      'CPC optimization',
      'ROAS optimization',
      'Campaign scaling',
      'Ad performance reporting',
    ],
  },
  {
    title: 'Inventory & Order Management',
    gradient: 'linear-gradient(135deg, #db2777, #e879f9)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
        <line x1="12" y1="22.08" x2="12" y2="12" />
      </svg>
    ),
    points: [
      'Inventory forecasting',
      'Stock planning',
      'Inventory monitoring',
      'Order tracking support',
      'Return management guidance',
      'Replenishment planning',
      'Performance monitoring',
    ],
  },
  {
    title: 'Pricing & Profitability Optimization',
    gradient: 'linear-gradient(135deg, #16a34a, #10b981)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </svg>
    ),
    points: [
      'Pricing strategy development',
      'Competitor pricing analysis',
      'Margin optimization',
      'Discount planning',
      'Promotional pricing',
      'Seasonal pricing strategies',
    ],
  },
  {
    title: 'Seller Account Health Management',
    gradient: 'linear-gradient(135deg, #b45309, #d97706)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    ),
    points: [
      'Account monitoring',
      'Performance score tracking',
      'Compliance support',
      'Operational issue resolution',
      'Seller panel management',
      'Policy guidance',
    ],
  },
  {
    title: 'Analytics & Reporting',
    gradient: 'linear-gradient(135deg, #475569, #334155)',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    points: [
      'Sales reports',
      'Catalog performance reports',
      'Advertising reports',
      'Inventory analysis',
      'Profitability tracking',
      'Growth dashboards',
      'Business intelligence reporting',
    ],
  },
];

const DIFFERENTIATORS = [
  'Dedicated marketplace specialists',
  'Meesho-focused expertise',
  'SEO + Advertising integration',
  'Performance-driven optimization',
  'Transparent reporting',
  'Scalable growth strategies',
  'End-to-end account management',
];

const INDUSTRIES = [
  'Fashion & Apparel',
  "Women's Wear",
  'Home & Kitchen',
  'Beauty & Personal Care',
  'Electronics Accessories',
  'Consumer Goods',
  'FMCG',
  'Health & Wellness',
  'Pet Products',
  'D2C Brands',
];

const STAGES = [
  'New catalog launches',
  'Product positioning',
  'Visibility enhancement',
  'Launch promotions',
  'Festival campaigns',
  'Seasonal sales',
  'Catalog promotions',
  'Revenue scaling strategies',
];

const ADDITIONAL = [
  'Sponsored product scaling',
  'Category diversification',
  'Multi-catalog growth',
  'Profitability analysis',
  'Competitor benchmarking',
  'Product portfolio expansion',
  'Review & rating monitoring',
  'Operational efficiency support',
];

const FAQS = [
  {
    q: 'Do you help with Meesho seller registration?',
    a: 'Yes. We assist with account registration, GST verification, store setup, banking configuration, and onboarding support.',
  },
  {
    q: 'Can you manage Meesho Ads?',
    a: 'Yes. We create, optimize, and scale Meesho advertising campaigns to improve visibility and sales performance.',
  },
  {
    q: 'Do you optimize product catalogs?',
    a: 'Yes. We optimize titles, attributes, descriptions, categories, pricing, and overall catalog structure to improve discoverability and conversions.',
  },
  {
    q: 'Can you manage inventory and operations?',
    a: 'Yes. We help monitor inventory, track order performance, plan stock replenishment, and improve operational efficiency.',
  },
  {
    q: 'Do you provide reporting?',
    a: 'Yes. We provide detailed performance reports covering sales, advertising, inventory, catalog performance, and growth opportunities.',
  },
];

export default function MeeshoPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const toggle = (i) => setOpenFaq(openFaq === i ? null : i);

  return (
    <>
      <SEOHead {...SEO} faqs={FAQS} />
      <Header />
      <main className="sp-page">

        {/* BREADCRUMB */}
        <nav className="sp-breadcrumb">
          <div className="sp-breadcrumb-inner">
            <Link to="/">Home</Link>
            <span className="sp-bc-sep">/</span>
            <Link to="/#services">Services</Link>
            <span className="sp-bc-sep">/</span>
            <span className="sp-bc-current">Meesho Marketplace</span>
          </div>
        </nav>

        {/* HERO */}
        <section className="sp-hero">
          <div className="sp-hero-inner">
            <div className="sp-hero-tag">Meesho Marketplace</div>
            <h1 className="sp-hero-h1">Meesho Marketplace Management Services</h1>
            <p className="sp-hero-sub">Complete Meesho Seller Account Management & Growth Solutions</p>
            <p className="sp-hero-desc">Grow your business on Meesho with professional seller account management services designed to increase visibility, improve conversions, optimize advertising campaigns, and scale revenue. We help manage every aspect of your Meesho business — from catalog optimization and advertising to inventory management, seller account support, and marketplace growth strategies.</p>
            <div className="sp-hero-stats">
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">0%</span><span className="sp-hero-stat-lbl">Commission Model</span></div>
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">PAN India</span><span className="sp-hero-stat-lbl">Delivery Network</span></div>
              <div className="sp-hero-stat"><span className="sp-hero-stat-val">Full</span><span className="sp-hero-stat-lbl">Account Management</span></div>
            </div>
            <div className="sp-hero-btns">
              <a href="/#contact" className="sp-btn-primary">Get Free Audit</a>
              <a href="#services" className="sp-btn-outline">Explore Services</a>
            </div>
          </div>
        </section>

        {/* PLATFORM OVERVIEW — with Image 1 */}
        <section className="sp-sec" id="overview">
          <div className="sp-sec-inner">
            <div className="sp-sec-img-grid">
              <div>
                <div className="sp-label">Platform Overview</div>
                <h2 className="sp-h2">Sell & Scale on Meesho with a Performance-Driven Strategy</h2>
                <p className="sp-para">Meesho is one of India's fastest-growing e-commerce marketplaces that enables businesses to sell products online through the Meesho Supplier Panel. Sellers can manage catalogs, orders, inventory, advertising campaigns, payments, and business performance through a centralized dashboard.</p>
                <p className="sp-para">Selling on Meesho gives your brand:</p>
                <ul className="sp-bullet-list">
                  {OVERVIEW_POINTS.map((pt, i) => <li key={i} className="sp-bullet-item"><span className="sp-dot" />{pt}</li>)}
                </ul>
              </div>
              <div className="sp-img-box">
                <img src={img1} alt="Meesho marketplace management" />
              </div>
            </div>
          </div>
        </section>

        {/* HOW WE HELP */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <div className="sp-label">How We Help Your Brand</div>
            <h2 className="sp-h2">End-to-End Meesho Marketplace Management</h2>
            <p className="sp-para">Success on Meesho requires more than uploading products. Strong marketplace performance depends on optimized catalogs, competitive pricing, advertising strategies, inventory planning, account management, and continuous optimization.</p>
            <h3 className="sp-h3">Our team acts as your dedicated Meesho growth partner by managing:</h3>
            <div className="sp-approach-grid">
              {APPROACH.map((item, i) => <div key={i} className="sp-approach-tag">{item}</div>)}
            </div>
            <p className="sp-para">We focus on sustainable growth through performance optimization, data-driven strategies, and conversion-focused execution tailored for the Meesho marketplace.</p>
          </div>
        </section>

        {/* OUR PROCESS */}
        <section className="sp-sec" id="process">
          <div className="sp-sec-inner">
            <div className="sp-label">Our Process</div>
            <h2 className="sp-h2">How We Work</h2>
            <div className="sp-process-list">
              {PROCESS.map((step, i) => (
                <div key={i} className="sp-process-step">
                  <div className="sp-process-left">
                    <div className="sp-process-num">{step.num}</div>
                    {i < PROCESS.length - 1 && <div className="sp-process-line" />}
                  </div>
                  <div className="sp-process-body">
                    <h3 className="sp-process-title">{step.title}</h3>
                    <p className="sp-process-intro">{step.intro}</p>
                    <ul className="sp-process-subs">
                      {step.points.map((pt, j) => <li key={j} className="sp-process-sub"><span className="sp-dot-sm" />{pt}</li>)}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section className="sp-sec sp-sec--alt" id="services">
          <div className="sp-sec-inner">
            <div className="sp-label">Services We Provide</div>
            <h2 className="sp-h2">Everything Your Meesho Business Needs</h2>
            <div className="sp-cards">
              {SERVICES.map((s, i) => (
                <div key={i} className="sp-card">
                  <div className="sp-card-accent" style={{ background: s.gradient }} />
                  <div className="sp-card-icon" style={{ background: s.gradient }}>{s.icon}</div>
                  <h3 className="sp-card-title">{s.title}</h3>
                  <ul className="sp-card-list">
                    {s.points.map((pt, j) => <li key={j} className="sp-card-item"><span className="sp-dot-sm" />{pt}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHY CHOOSE — with Image 2 */}
        <section className="sp-sec">
          <div className="sp-sec-inner">
            <div className="sp-label">Why Choose Our Meesho Marketplace Services</div>
            <h2 className="sp-h2">Marketplace Experts Focused on Growth</h2>
            <p className="sp-para">We combine catalog optimization, marketplace strategy, advertising management, inventory planning, and performance analytics to help brands scale efficiently on Meesho.</p>
            <div className="sp-why-grid">
              <div>
                <h3 className="sp-h3">Benefits of Working With Us</h3>
                <ul className="sp-diff-list">
                  {DIFFERENTIATORS.map((d, i) => <li key={i} className="sp-diff-item"><span className="sp-check" />{d}</li>)}
                </ul>
              </div>
              <div className="sp-img-box">
                <img src={img2} alt="Meesho marketplace management approach" />
              </div>
            </div>
            <h3 className="sp-h3">Industries We Serve</h3>
            <div className="sp-industry-wrap">
              {INDUSTRIES.map((ind, i) => <span key={i} className="sp-industry-chip">{ind}</span>)}
            </div>
          </div>
        </section>

        {/* BUSINESS STAGE */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <h2 className="sp-h2">Marketplace Growth Solutions for Every Stage</h2>
            <p className="sp-para">Whether you're looking to grow through:</p>
            <div className="sp-stage-grid">
              {STAGES.map((s, i) => <div key={i} className="sp-stage-item"><span className="sp-dot" />{s}</div>)}
            </div>
            <p className="sp-para">Our team helps create a structured marketplace growth strategy tailored to your business goals on Meesho.</p>
          </div>
        </section>

        {/* ADDITIONAL SERVICES */}
        <section className="sp-sec">
          <div className="sp-sec-inner">
            <div className="sp-label">Additional Growth Services</div>
            <div className="sp-additional-grid">
              {ADDITIONAL.map((item, i) => <div key={i} className="sp-additional-item"><span className="sp-check" />{item}</div>)}
            </div>
          </div>
        </section>

        {/* FAQ — accordion */}
        <section className="sp-sec sp-sec--alt">
          <div className="sp-sec-inner">
            <div className="sp-label">Frequently Asked Questions</div>
            <h2 className="sp-h2">Got Questions? We Have Answers.</h2>
            <div className="sp-faq-list">
              {FAQS.map((faq, i) => (
                <div key={i} className={`sp-faq-card${openFaq === i ? ' sp-faq-card--open' : ''}`}>
                  <button className="sp-faq-header" onClick={() => toggle(i)} aria-expanded={openFaq === i}>
                    <span className="sp-faq-q">{faq.q}</span>
                    <span className="sp-faq-chevron">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                    </span>
                  </button>
                  {openFaq === i && <div className="sp-faq-body"><div className="sp-faq-a">{faq.a}</div></div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="sp-cta">
          <div className="sp-cta-inner">
            <h2 className="sp-cta-h2">Ready to Grow on Meesho?</h2>
            <p className="sp-cta-desc">Scale your business with complete Meesho Marketplace Management Services focused on catalog optimization, advertising performance, seller account management, inventory planning, and long-term marketplace growth.</p>
            <a href="/#contact" className="sp-btn-white">Get Free Audit</a>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
